import { Inject, Injectable, DOCUMENT, Injector } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Preferences } from '@capacitor/preferences';
import { Company, Module, User } from 'src/app/models/user.model';
import { CompanyType, StorageKey } from 'src/app/shared/enums';
import { environment } from 'src/environments/environment';

import { UtilsService } from 'src/app/services/util/util-service';
import { BrowserService } from 'src/app/services/browser/browser.service';
import { FileStorageSetting } from '@app/models/filestorage.model';
import { Router } from '@angular/router';
import { LocalizationService } from '@app/components/internationalization/localization.service';
import { Browser } from '@capacitor/browser';
import { ReferenceService } from '../reference/reference.service';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { UserService } from '../user/user.service';
import { AndroidOptions, GenericOAuth2, IosOptions, OAuth2AuthenticateOptions, OAuth2RefreshTokenOptions } from '@capacitor-community/generic-oauth2';
import { WebUtils } from '@capacitor-community/generic-oauth2/dist/esm/web-utils.js';
import { MsalService } from '@azure/msal-angular';
import { IdTokenClaims, RedirectRequest } from '@azure/msal-browser';
import { GeolocationService } from '../geolocation/geolocation.service';
import { AuthCallbackResult, AuthPolicyResult, MsalTokenResponse, OAuthCallbackTokenResponse, OAuthRedirectQueryParams, OAuthTokenResponse, RoleModule, toOAuthCallbackTokenResponse, toOAuthTokenResponse } from '@app/models/auth-token.model';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private oauth2Options: OAuth2AuthenticateOptions;
  private oauth2RefreshOptions: OAuth2RefreshTokenOptions;
  readonly KEY = StorageKey[StorageKey.USER];
  readonly KEY_TOKEN = StorageKey[StorageKey.TOKEN];
  readonly KEY_REFRESHTOKEN = StorageKey[StorageKey.REFRESHTOKEN];
  readonly KEY_MODULE = StorageKey[StorageKey.MODULE];
  readonly KEY_FILESETTINGS = StorageKey[StorageKey.FILESETTINGS];
  readonly KEY_TDSID = StorageKey[StorageKey.TDSID];
  readonly KEY_CURRENT_PROFILE = StorageKey[StorageKey.CURRENTPROFILE];
  private isResourceAdminState = new BehaviorSubject<boolean>(false);
  private _initMsalPromise: Promise<void> | null = null;

  constructor(
    private _jwtHelper: JwtHelperService,
    @Inject(DOCUMENT) private document: Document,
    private _utilsService: UtilsService,
    private _router: Router,
    private _localizationService: LocalizationService,
    private _browserService: BrowserService,
    private _referenceService: ReferenceService,
    private _userService: UserService,
    private _msalService: MsalService,
    private _injector: Injector
  ) {

    this.oauth2Options = {
      appId: environment.authB2C.appId,
      scope: environment.authB2C.scope,
      pkceEnabled: true,
      web: {
        responseType: 'token',
        accessTokenEndpoint: '',
        redirectUrl: this.appendRedirectPath(window.location.origin),
        windowOptions: 'height=600, Width=400, left=0, top=0',
        windowTarget: '_self'
      },
      android: {
        responseType: 'code',
        redirectUrl: 'cityonsite://auth'
      },
      ios: {
        pkceEnabled: true,
        responseType: 'code',
        redirectUrl: 'cityonsite://auth'
      }
    };

    this.oauth2RefreshOptions = {
      appId: environment.authB2C.appId,
      scope: environment.authB2C.scope,
      accessTokenEndpoint: environment.authB2C.accessTokenEndpoint.replace('{azure_policy}', environment.b2cSignInPolicy),
      refreshToken: ''
    };
  }

  public async getUser(): Promise<User> {
    const item = await Preferences.get({ key: this.KEY });
    var obj;
    if (item.value != null) {
      obj = JSON.parse(item.value);
    }
    return obj;
  }

  public async getCurrentProfile(): Promise<string | null> {
    const item = await Preferences.get({ key: this.KEY_CURRENT_PROFILE });
    return item.value;
  }

  public async setCurrentProfile(companyId: string): Promise<boolean> {
    const companies = await this.getProfileCompanies();
    if (!companies.some(company => company.id === companyId)) {
      return false;
    }
    await Preferences.set({ key: this.KEY_CURRENT_PROFILE, value: companyId });
    return true;
  }

  public async getProfileCompanies(): Promise<Company[]> {
    try {
      const storedUser = localStorage.getItem(this.KEY);
      const user: User = storedUser ? JSON.parse(storedUser) : await this.getUser();
      const companies = Array.isArray(user?.companies)
        ? user.companies.filter(company => !!company?.id) : [];
      return companies.some(company => company.companyType === CompanyType.SELF)
        ? companies.filter(company => company.companyType === CompanyType.SELF)
        : companies;
    } catch {
      return [];
    }
  }

  public async clearCurrentProfile(): Promise<void> {
    await Preferences.remove({ key: this.KEY_CURRENT_PROFILE });
  }

  async setUser(user: User) {

    //we dont want to persist debug mode in storage, hence always set to false
    user.isDebugModeActive = false;

    await Preferences.set({
      key: StorageKey[this.KEY],
      value: JSON.stringify(user),
    });
  }

  public async getUserModule(): Promise<Module[]> {
    const item = await Preferences.get({ key: this.KEY_MODULE });
    var obj;

    if (item.value != null) {
      obj = JSON.parse(item.value);
    }
    return obj;
  }

  async setUserModule(module: Module[]) {
    let filteredModule: Module[] = [];

    if (this._utilsService.isAndroid()) {
      filteredModule = module.filter(m => m.enableAndroid);
    } else if (this._utilsService.isIOS()) {
      filteredModule = module.filter(m => m.enableIOS);
    } else if (this._utilsService.isWebBrowser() || this._utilsService.isEdgeBrowser()) {
      filteredModule = module.filter(m => m.enableWeb);
    }

    await Preferences.set({
      key: StorageKey[this.KEY_MODULE],
      value: JSON.stringify(filteredModule),
    });
  }

  public async getFileSettings(): Promise<FileStorageSetting> {
    const item = await Preferences.get({ key: this.KEY_FILESETTINGS });
    var obj;
    if (item.value != null) {
      obj = JSON.parse(item.value);
    }
    return obj;
  }

  async setFileSettings(setting: FileStorageSetting) {
    await Preferences.set({
      key: StorageKey[this.KEY_FILESETTINGS],
      value: JSON.stringify(setting),
    });
  }

  async getTdsId(): Promise<string> {
    const item = await Preferences.get({ key: this.KEY_TDSID });
    return item.value!;
  }

  async setTdsId(dsid: string) {
    await Preferences.set({
      key: StorageKey[this.KEY_TDSID],
      value: dsid,
    });
  }

  public async getTokenFromStorage(): Promise<string | null> {
    return Preferences.get({ key: this.KEY_TOKEN })
      .then(async item => {
        if (item?.value) {
           if (!this._utilsService.isMobileAndTablet()) {
              await this.handleMsalCallBack();
              const account = this._msalService.instance.getActiveAccount();
              if (account) {
                if (this.isTokenValid(item.value)) {
                    return item?.value;
                } else {
                  const request = {
                    scopes: environment.authB2C.scope.split(' '),
                    account: account
                  };
                  try {
                    const result = await firstValueFrom(this._msalService.acquireTokenSilent(request));
                    const token = await this.setMsalTokenInStorage(result) as string;
                    return token;
                  } catch (error) {
                    console.error('Silent token acquisition failed. Acquiring token interactively.', error);
                  }
                }
              }
              await this.logoutUser(item?.value!, this.getRedirectUri(), false);
              return null;
           }
           else {
              if (this.isTokenValid(item.value)) {
                return item?.value;
              } else {
                return Preferences.get({ key: this.KEY_REFRESHTOKEN })
                .then(refreshToken => {
                  if (refreshToken && refreshToken.value != null && refreshToken.value != '') {
                    this.oauth2RefreshOptions.refreshToken = refreshToken.value;
                    try {

                      const decodedToken = this._jwtHelper.decodeToken(item?.value!);
                      if(decodedToken && decodedToken.loginType){
                        if(decodedToken.loginType == 'apple'){
                          this.oauth2RefreshOptions.accessTokenEndpoint = environment.authB2C.accessTokenEndpoint.replace('{azure_policy}', environment.b2cSignInWithApplePolicy);
                        }else if(decodedToken.loginType == 'google'){
                          this.oauth2RefreshOptions.accessTokenEndpoint = environment.authB2C.accessTokenEndpoint.replace('{azure_policy}', environment.b2cGoogleSignUpOrSignin);
                        }
                      }
                     
                      return GenericOAuth2.refreshToken(this.oauth2RefreshOptions)
                        .then(
                          resolve => {
                            return this.setTokenInStorage(resolve, true);
                          },
                          rejects => {
                            alert("reject :" + JSON.stringify(rejects));
                            return Preferences.clear()
                              .then(_ => {
                                return this._router.navigate(['login'])
                              });
                          }
                        )
                    }
                    catch (error) {
                      alert(this._localizationService.translate('auth_refresh_token_error'));
                      return this._router.navigate(['login']);
                    }
                  } else {
                    return this._router.navigate(['login']);
                  }
                })

              }
           }
        } else return null;
      })
      //Redirect /logout paths resolve to a non-token value (e.g router.navigate result);
      .then(async result => {
        if (typeof result === 'string') {
          return result;
        }
        await this.clearCurrentProfile();
        return null;
      })
      .catch(async error => {
        await this.clearCurrentProfile();
        throw error;
      });
  }

  public async setTokenInStorage(resourceUrlResponse: OAuthTokenResponse | OAuthCallbackTokenResponse | OAuthRedirectQueryParams, refresh: boolean): Promise<string> {
    if (this.isDevice()) {
      if (refresh) {
        const tokenResponse = toOAuthTokenResponse(resourceUrlResponse);

        await Preferences.set({
          key: StorageKey[this.KEY_TOKEN],
          value: tokenResponse.access_token
        });

        await Preferences.set({
          key: StorageKey[this.KEY_REFRESHTOKEN],
          value: tokenResponse.refresh_token ?? ''
        });

        return tokenResponse.access_token;
      }
      else {
        const callbackResponse = toOAuthCallbackTokenResponse(resourceUrlResponse);

        await Preferences.set({
          key: StorageKey[this.KEY_TOKEN],
          value: callbackResponse.access_token_response.access_token
        });

        await Preferences.set({
          key: StorageKey[this.KEY_REFRESHTOKEN],
          value: callbackResponse.access_token_response.refresh_token ?? ''
        });

        return callbackResponse.access_token_response.access_token;
      }
    }
    else {
      const tokenResponse = toOAuthTokenResponse(resourceUrlResponse);
      await Preferences.set({
        key: StorageKey[this.KEY_TOKEN],
        value: tokenResponse.access_token
      });
      return tokenResponse.access_token;
    }
  }

  public async getSSODomain(): Promise<boolean> {
    return await new Promise<boolean>((resolve, reject) => {
      this.getTokenFromStorage()
        .then(token => {
          if (!token) {
            reject(this._localizationService.translate('auth_service_get_token_error'));
            return;
          }
          const decodedToken = this._jwtHelper.decodeToken(token);
          resolve(decodedToken.isSSODomain);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public async getLoginType(): Promise<string> {
    return await new Promise<string>((resolve, reject) => {
      this.getTokenFromStorage()
        .then(token => {
          if (!token) {
            reject(this._localizationService.translate('auth_service_get_token_error'));
            return;
          }
          const decodedToken = this._jwtHelper.decodeToken(token);
          resolve(decodedToken.loginType?.toLowerCase() || '');
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public async getUserRoles(): Promise<string[]> {
    return await new Promise<string[]>((resolve, reject) => {
      this.getTokenFromStorage()
        .then(token => {
          if (!token) {
            reject(this._localizationService.translate('auth_service_get_token_error'));
            return;
          }
          const decodedToken = this._jwtHelper.decodeToken(token);
          let roles: string[] = [];
          if (decodedToken) {
            const modules = JSON.parse(decodedToken.extension_role) as RoleModule[];

            if (Array.isArray(modules)) {
              modules.forEach(m => {
                const objs = roles.filter(r => r == m.RoleName)
                if (!(objs && objs.length > 0)) {
                  roles.push(m.RoleName);
                }
              });
            }
          }

          resolve(roles);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  async isAuthenticated(): Promise<boolean> {
    const item = await Preferences.get({ key: this.KEY });
    const token = await Preferences.get({ key: this.KEY_TOKEN });

    if (
      token == null ||
      token.value == null ||
      !this.isTokenValid(token.value) ||
      item.value == null) {
      await this.clearCurrentProfile();
      return false;
    }

    const itemValue = JSON.parse(item.value);
    const itemValueArr = Array.isArray(itemValue.companies) ? itemValue.companies : (itemValue.companies ? [itemValue.companies] : []);

    for (let i = 0; i < itemValueArr.length; i++) {
      const company = itemValueArr[i];
      if (company.companyType === CompanyType.SELF) {
        this.setResourceAdminState(true);
        break;
      }
    }

    if (item.value != null) {
      return true;
    }
    return false;
  }

  getResourceAdminState() {
    return this.isResourceAdminState.asObservable();
  }

  setResourceAdminState(state: boolean) {
    this.isResourceAdminState.next(state);
  }

  async handleCallBack(url: string): Promise<AuthCallbackResult | undefined> {
    const urlParamObj = WebUtils.getUrlParams(url);

    if (urlParamObj && urlParamObj['access_token'] != null && urlParamObj['state'] != null) {
      const token = await this.setTokenInStorage(urlParamObj, false) as string;
      const validUser = this.isTokenValid(token);

      let decodedToken = this._jwtHelper.decodeToken(token);
      if (decodedToken && decodedToken.dsid && validUser) {
        await this.setTdsId(decodedToken.dsid);
      }

      return { policy: urlParamObj['state'], azureId: (validUser) ? this.getAzureObjectIdFromToken(token) : '' };
    }
    else if (urlParamObj && urlParamObj['error'] !== null && (urlParamObj['state'] === environment.b2cGoogleSignUpOrSignin || urlParamObj['state'] === environment.b2cSignInWithApplePolicy)) {
      alert(this._localizationService.translate('loginuser_signin_with_appleorgoogle_registration_error'));
      return undefined;
    }
    else{
      return new Promise<never>((_, reject) => {
        reject('Oauth parameters are not present in return url');
      });
    }
  }

  public async loginToAzureB2C(userEmail: string, policy: string): Promise<string> {
     if (this._utilsService.isMobileAndTablet()) {
          return await this.loginMobile(userEmail, policy);
    } else {
          // Web flow (MSAL)
          return await this.loginWeb(userEmail, policy);
    }
  }

  public async loginWithApple() {
    try {
      this.oauth2Options.authorizationBaseUrl = environment.authB2C.authorizationBaseUrl.replace('{azure_policy}', environment.b2cSignInWithApplePolicy);
      this.oauth2Options.accessTokenEndpoint = environment.authB2C.accessTokenEndpoint.replace('{azure_policy}', environment.b2cSignInWithApplePolicy);
      this.oauth2Options.state = environment.b2cSignInWithApplePolicy;

      if (await this._utilsService.isMobileAndTablet()) {
        const resourceUrlResponse = await GenericOAuth2.authenticate(this.oauth2Options);

        if (resourceUrlResponse.error) {
          alert('response error: ${resourceUrlResponse.error} - ${resourceUrlResponse.error_description}');

          return '';
        }
        
        const token = await this.setTokenInStorage(resourceUrlResponse, false) as string;
        const validUser = this.isTokenValid(token);

        return (validUser) ? this.getAzureObjectIdFromToken(token) : '';
      }
      else {
        return this.loginWeb('', environment.b2cSignInWithApplePolicy);
      }
    } catch (error) {
      alert(this._localizationService.translate('loginuser_signin_with_appleorgoogle_registration_error'));

      return '';
    }
  }

  public async loginUsingGoogle(): Promise<string> {
    try {
      this.oauth2Options.authorizationBaseUrl = environment.authB2C.authorizationBaseUrl.replace('{azure_policy}', environment.b2cGoogleSignUpOrSignin);
      this.oauth2Options.accessTokenEndpoint = environment.authB2C.accessTokenEndpoint.replace('{azure_policy}', environment.b2cGoogleSignUpOrSignin);
      this.oauth2Options.state = environment.b2cGoogleSignUpOrSignin;

      if (await this._utilsService.isMobileAndTablet()) {
        const resourceUrlResponse = await GenericOAuth2.authenticate(this.oauth2Options);

        if (resourceUrlResponse.error) {
          alert('response error: ${resourceUrlResponse.error} - ${resourceUrlResponse.error_description}');
          return '';
        }

        const token = await this.setTokenInStorage(resourceUrlResponse, false) as string;
        const validUser = this.isTokenValid(token);
        return (validUser) ? this.getAzureObjectIdFromToken(token) : '';
      }
      else {
         return this.loginWeb('', environment.b2cGoogleSignUpOrSignin);
      }
    } catch (error) {
      alert(this._localizationService.translate('loginuser_signin_with_appleorgoogle_registration_error'));
    }
    return '';
  }

  async logout(): Promise<void> {
    await this.clearCurrentProfile();
    const token = (await this.getTokenFromStorage()) ?? '';
    let decodedToken = this._jwtHelper.decodeToken(token);
    let isSSODomain: boolean = false;
    if (decodedToken) {
      isSSODomain = decodedToken.isSSODomain;
      let logOutUrl = "";
      let url = "";

      await this._userService.clearOutOfRangeSettings();

      if (isSSODomain) {
        this._referenceService.getUserEmailDomain()
          .subscribe({
            next: (userDomain) => {
              if (userDomain) {
                url = `${userDomain.logoutURL}${this.getRedirectUri()}`;
                this.logoutUser(token, url, isSSODomain);
              }
              else {
                alert(this._localizationService.translate('auth_get_user_domain_not_found'));
              }
            },
            error: (reject) => {
              alert(this._localizationService.translate('auth_get_user_domain_error'));
            }
          });
      }
      else {
        logOutUrl = environment.authB2C.signOutRequestUrl.replace('{azure_policy}', environment.b2cSignInPolicy);
        url = `${logOutUrl}?post_logout_redirect_uri=${this.getRedirectUri()}`;
        await this.logoutUser(token, url, isSSODomain);
      }
    }
  }

  private async logoutUser(token: string, url: string, isSSODomain: boolean) {
    await this.clearCurrentProfile();
    let lang = await this._localizationService.get();
    let locale = await this._localizationService.getAzureLocale();
    let dsid = await this.getTdsId();
    const geoLocationService = this._injector.get(GeolocationService);

    await Preferences.clear();
    await this._localizationService.set(lang);
    await this._localizationService.setAzureLocale(locale);
    await geoLocationService.endLocationWatch();
    await this.setTdsId(dsid);
    
    if (this._utilsService.isMobileAndTablet()) {
      await GenericOAuth2.logout(this.oauth2Options, token);
      Browser.addListener('browserPageLoaded', async () => {
        Browser.removeAllListeners().then(s => { });
        await Browser.close();

        await this._router.navigate(['login']);
        this.reloadPage();
      });
      await Browser.open({ url });
    } else {
      await this.handleMsalCallBack();
      const account = this._msalService.instance.getActiveAccount();
      if (account) {
        await this._msalService.logoutRedirect({
          account: account,
          postLogoutRedirectUri: this.getRedirectUri()
        });
      }
    }
  }

  private isTokenValid(token: string | null): boolean {
    return !!token && !this._jwtHelper.isTokenExpired(token);
  }

  private getAzureObjectIdFromToken(token: string | null): string {
    if (token) {
      const decodedToken = this._jwtHelper.decodeToken(token);
      return decodedToken.sub;
    }
    return "";
  }

  public async resetPassword(userEmail: string): Promise<void> {
    const locale = await this._localizationService.getAzureLocale();
    const url = `${environment.authB2C.resetPasswordUrl}&client_id=${environment.authB2C.appId}&nonce=defaultNonce&redirect_uri=${this.appendRedirectPath(this.document.location.origin)}/userprofile&scope=openid&response_type=id_token&login_hint=${userEmail}&ui_locales=${locale}`;
    if (this.isDevice()) {

      this.oauth2Options.appId = environment.authB2C.appId;
      this.oauth2Options.scope = "openid";
      this.oauth2Options.state = environment.b2cForgotPasswordPolicy;
      this.oauth2Options.authorizationBaseUrl = environment.authB2C.authorizationBaseUrl.replace('{azure_policy}', environment.b2cForgotPasswordPolicy);
      this.oauth2Options.additionalParameters = { 'login_hint': userEmail, 'ui_locales': locale };
      let androidOption: AndroidOptions = {
        responseType: "id_token",
        redirectUrl: 'cityonsite://userprofile'
      };
      let iosOption: IosOptions = {
        pkceEnabled: true,
        responseType: "id_token",
        redirectUrl: 'cityonsite://userprofile'
      }
      this.oauth2Options.android = androidOption;
      this.oauth2Options.ios = iosOption;

      // Hack to allow ui_locales to be passed in the url for Android
      if(this._utilsService.isAndroid() && this._utilsService.isMobileAndTablet()){
        this.oauth2Options.authorizationBaseUrl = environment.authB2C.authorizationBaseUrl.replace('{azure_policy}', environment.b2cForgotPasswordPolicy) +"?ui_locales="+ locale;
        this.oauth2Options.additionalParameters = { 'login_hint': userEmail };
      }

      const resourceUrlResponse = await GenericOAuth2.authenticate(this.oauth2Options);
    }
    else {
      this.msalLogin(userEmail, environment.b2cForgotPasswordPolicy, '', '');
      return;
    }
  }

  public async updateUserDetails(currentUser: User): Promise<void> {
    const email = encodeURIComponent(currentUser.emailAddress);
    const locale = await this._localizationService.getAzureLocale();
    const url = `${environment.authB2C.editProfileUrl}&client_id=${environment.authB2C.appId}&nonce=defaultNonce&redirect_uri=${this.appendRedirectPath(this.document.location.origin)}/userprofile&scope=openid&response_type=id_token&email=${email}&ui_locales=${locale}`;
    if (this.isDevice()) {
      this.oauth2Options.appId = environment.authB2C.appId;
      this.oauth2Options.scope = "openid";
      this.oauth2Options.state = environment.b2cEditProfilePolicy;
      this.oauth2Options.authorizationBaseUrl = environment.authB2C.authorizationBaseUrl.replace('{azure_policy}', environment.b2cEditProfilePolicy);
      this.oauth2Options.additionalParameters = { 'email': currentUser.emailAddress, 'ui_locales': locale };
      let androidOption: AndroidOptions = {
        responseType: "id_token",
        redirectUrl: 'cityonsite://userprofile'
      };
      let iosOption: IosOptions = {
        pkceEnabled: true,
        responseType: "id_token",
        redirectUrl: 'cityonsite://userprofile'
      }
      this.oauth2Options.android = androidOption;
      this.oauth2Options.ios = iosOption;

      // Hack to allow ui_locales to be passed in the url for Android
      if(this._utilsService.isAndroid() && this._utilsService.isMobileAndTablet()){
        this.oauth2Options.authorizationBaseUrl = environment.authB2C.authorizationBaseUrl.replace('{azure_policy}', environment.b2cEditProfilePolicy) +"?ui_locales="+ locale;
        this.oauth2Options.additionalParameters = {};
      }

      const resourceUrlResponse = await GenericOAuth2.authenticate(this.oauth2Options);
    }
    else {
      this.msalLogin('', environment.b2cEditProfilePolicy, '', '');
      return;
    }
  }

  public async deactivateUser(currentUser: User): Promise<void> {
    const email = encodeURIComponent(currentUser.emailAddress);
    const url = `${environment.authB2C.deactivateProfileUrl}&client_id=${environment.authB2C.appId}&nonce=defaultNonce&redirect_uri=${this.document.location.origin}/logout/true&scope=openid&response_type=id_token&email=${email}&prompt=login`;

    if (this.isDevice()) {
      this.oauth2Options.authorizationBaseUrl = environment.authB2C.authorizationBaseUrl.replace('{azure_policy}', environment.b2cDeactivateProfilePolicy);
      this.oauth2Options.accessTokenEndpoint = environment.authB2C.accessTokenEndpoint.replace('{azure_policy}', environment.b2cDeactivateProfilePolicy);
      this.oauth2Options.state = environment.b2cDeactivateProfilePolicy;
      this.oauth2Options.additionalParameters = { 'login_hint': currentUser.emailAddress };

      let androidOption: AndroidOptions = {
        responseType: "code",
        redirectUrl: 'cityonsite://logout/true'
      };
      let iosOption: IosOptions = {
        pkceEnabled: true,
        responseType: "code",
        redirectUrl: 'cityonsite://logout/true'
      }
      this.oauth2Options.android = androidOption;
      this.oauth2Options.ios = iosOption;
      try {
        const resourceUrlResponse = await GenericOAuth2.authenticate(this.oauth2Options);
        if (resourceUrlResponse.error) {
          throw new Error(`${resourceUrlResponse.error} - ${resourceUrlResponse.error_description}`);
        }
      }
      catch (error) {
        throw new Error(`${error}`);
      }
      return;
    }
    else {
      this.msalLogin(email, environment.b2cDeactivateProfilePolicy, '', '');
      await this.logout();
      this._msalService.instance.clearCache();
      return;
    }
  }

  getRedirectUri(): string {
    return this.isDevice() ? 'cityonsite://auth' : this.appendRedirectPath(this.document.location.origin);
  }

  private isDevice(): boolean {
    return this._utilsService.isMobileAndTablet();
  }

  async clearLocalStorage() {
    await this.clearCurrentProfile();
    await Preferences.clear();
  }

  async clearToken() {
    await this.clearCurrentProfile();
    await Preferences.remove({ key: StorageKey[this.KEY_TOKEN] });
  }

  private appendRedirectPath(url: string): string {
    return this._utilsService.isNotAndroidOrIOS() ? url : url + '/redirect';
  }

  reloadPage() {
    window.location.reload();
  }

  private loginMobile(userEmail: string, policy: string) : Promise<string> {
     return this.getTokenFromStorage().then(async token => {
      if (token && token != 'undefined') {
        const validUser = this.isTokenValid(token);
        if (validUser) {
          return this.getAzureObjectIdFromToken(token);
        }
      }
      let locale = await this._localizationService.getAzureLocale();
      let dsid = await this.getTdsId();
      if(!(dsid)) {
        dsid = "00000000-0000-0000-0000-000000000000";
      }
      
      this.oauth2Options.authorizationBaseUrl = environment.authB2C.authorizationBaseUrl.replace('{azure_policy}', policy);
      this.oauth2Options.accessTokenEndpoint = environment.authB2C.accessTokenEndpoint.replace('{azure_policy}', policy);
      
      if (policy == environment.b2cSignUpPolicy) {
        this.oauth2Options.additionalParameters = { 'dsid': dsid, 'ui_locales': locale };
        this.oauth2Options.state = environment.b2cSignUpPolicy;
      } else {
        this.oauth2Options.additionalParameters = { 'dsid': dsid,'login_hint': userEmail, 'ui_locales': locale };
        this.oauth2Options.state = environment.b2cSignInPolicy;
      }

      // Hack to allow ui_locales to be passed in the url for Android
      if(this._utilsService.isAndroid()){
        this.oauth2Options.authorizationBaseUrl = environment.authB2C.authorizationBaseUrl.replace('{azure_policy}', policy)+"?ui_locales="+ locale;
        
        if (policy == environment.b2cSignUpPolicy) {
          this.oauth2Options.additionalParameters = { 'dsid': dsid };
        } else {
          this.oauth2Options.additionalParameters = { 'dsid': dsid, 'login_hint': userEmail };
        }
      }

      const resourceUrlResponse = await GenericOAuth2.authenticate(this.oauth2Options);

      if (resourceUrlResponse.error) {
        throw new Error(`${resourceUrlResponse.error} - ${resourceUrlResponse.error_description}`);
      }

      const tokenReturn = await this.setTokenInStorage(resourceUrlResponse, false) as string;
      const validUser = this.isTokenValid(tokenReturn);

      let decodedToken = this._jwtHelper.decodeToken(tokenReturn);
      if (decodedToken && decodedToken.dsid && validUser) {
        await this.setTdsId(decodedToken.dsid);
      }

      return (validUser) ? this.getAzureObjectIdFromToken(tokenReturn) : '';

    });
  }

  private async loginWeb(userEmail: string, policy: string): Promise<string> {
     return this.getTokenFromStorage().then(async token => {
      if (token && token != 'undefined') {
        const validUser = this.isTokenValid(token);
        if (validUser) {
          return this.getAzureObjectIdFromToken(token);
        }
      }

      const [locale, dsidRaw] = await Promise.all([
        this._localizationService.getAzureLocale(),
        this.getTdsId()
      ]);

      const dsid = dsidRaw || '00000000-0000-0000-0000-000000000000';

      this.msalLogin(userEmail, policy, locale, dsid);
      return '';
    });
   
  }

  private msalLogin(userEmail: string, policy: string, locale: string, dsid: string): void {
     const request : RedirectRequest = {
        authority: this.getMsalAuthority(policy),
        scopes: environment.authB2C.scope.split(' '),
        loginHint: userEmail,
        state: policy,
        extraQueryParameters: {
          ui_locales: locale,
          dsid
        }
      };

      this._msalService.loginRedirect(request);
  }

  private getMsalAuthority(policy: string): string {
    return `https://${environment.authB2C.tenant}.b2clogin.com/${environment.authB2C.tenant}.onmicrosoft.com/${policy}`;
  }

  public async setMsalTokenInStorage(resourceUrlResponse: MsalTokenResponse): Promise<string> {
 
    await Preferences.set({
      key: StorageKey[this.KEY_TOKEN],
      value: resourceUrlResponse.accessToken
    });

    return resourceUrlResponse.accessToken;
  }

  handleMsalCallBack(): Promise<void> {
    if (!this._initMsalPromise) {
        this._initMsalPromise = firstValueFrom(
            this._msalService.handleRedirectObservable()
        ).then(result => {
            const account = result?.account 
                ?? this._msalService.instance.getAllAccounts()[0];

            if (account) {
                this._msalService.instance.setActiveAccount(account);
            }
        });
    }

    return this._initMsalPromise;
  }

  async handleMsalLoginCallBack(): Promise<AuthPolicyResult | undefined> {
    try {
      const result = await firstValueFrom(
        this._msalService.handleRedirectObservable()
      );

      if (result?.account && result?.idTokenClaims && result?.accessToken) {
        const token = await this.setMsalTokenInStorage(result) as string;
        const validUser = this.isTokenValid(token);

        this._msalService.instance.setActiveAccount(result.account);
        let decodedToken = this._jwtHelper.decodeToken(token);
        if (decodedToken && decodedToken.dsid && validUser) {
          await this.setTdsId(decodedToken.dsid);
        }

        const claims = result.idTokenClaims as IdTokenClaims;
        const tfp = claims.tfp;
        const acr = claims.acr;

        return {
          policy: (typeof tfp === 'string' ? tfp.toUpperCase() : undefined) 
            ?? (typeof acr === 'string' ? acr.toUpperCase() : '')
        };
      }
      return undefined;
    } catch (error: unknown) {
      console.error('Msal login error:', error);
      const errorMessage = (error as { errorMessage?: string } | null)?.errorMessage;

      return new Promise<never>((_, reject) => {
        reject('Msal login error: ' + errorMessage);
      });
    }

  }
}